export interface FeatureCard {
  description?: string;
  /** Stacked cascading mini-cards (Fragmentation section). Icon is resolved
   * from `label` via stackedIconForLabel in FeatureCard.tsx — real SVG
   * stroke icons, not emoji. */
  items?: { label: string; value?: string }[];
  stackedItems?: boolean;
  /** Side-by-side sub-cards (Unified Platform section) */
  subCards?: { title: string; rows: { label: string; value: string }[] }[];
  /** Revenue overview block (Revenue Flow section) */
  overview?: { title?: string; amount: string; subtitle: string };
  /** Stats with label + large value (Revenue Flow section) */
  stats?: { label: string; value: string }[];
  /** Grouped data sections with indicators (Revenue Intelligence section) */
  dataSections?: {
    title: string;
    indicator?: 'danger' | 'warning';
    rows?: { label: string; value: string }[];
    miniChart?: boolean;
    /** Shown above the sparkline (e.g. "Pro users" inside Expansion card) */
    chartCaption?: string;
  }[];
  /** Footer callout */
  footer?: { text: string; highlight?: string };
  /** Show Revvly brand card at bottom */
  brandCard?: boolean;
  /** Module tags for isometric-style grid (Platform Modules section) */
  modules?: string[];
  /** Draw a connector rail linking the stacked mini-cards and recolor the
   * footer window to the positive/mint "connected" state (section 1). */
  connected?: boolean;
}

export interface CameraPosition {
  x: number;
  y: number;
  scale: number;
  rotate?: number;
}

export interface Section {
  id: string;
  navLabel: string;
  headline: string;
  accentWord: string;
  camera: CameraPosition;
  subtitle?: string;
  /** Descriptive lede rendered as a paragraph under the headline. */
  sub?: string;
  secondSubtitle?: string;
  ctas?: { label: string; icon: string; variant: 'primary' | 'secondary'; href?: string }[];
  featureCard?: FeatureCard;
  featureCardPosition?: 'left' | 'right' | 'center';
  /** CTA below section content (e.g. last slide) */
  footerCta?: { label: string; to: string };
}

export const sections: Section[] = [
  {
    // Rendered by the dedicated <HeroSection /> (founding-cohort hero); these
    // fields feed only the ticker label and the scroll-parallax camera.
    id: 'hero',
    navLabel: 'Hero',
    headline: 'Orchestrate the\nflow of',
    accentWord: 'revenue',
    camera: { x: 0, y: 0, scale: 0.8 },
  },
  {
    id: 'fragmentation',
    navLabel: 'One platform',
    headline: 'One platform for your\nentire revenue',
    accentWord: 'lifecycle',
    camera: { x: 18, y: 15, scale: 1.05, rotate: 3 },
    sub: 'Billing, pricing, payments, CRM, and analytics, working together on a single data layer.',
    featureCardPosition: 'left',
    featureCard: {
      description:
        'Every part of your revenue, <strong>connected in one system.</strong>',
      items: [
        { label: 'Billing', value: 'Payments' },
        { label: 'CRM', value: 'Customers' },
        { label: 'Analytics', value: 'Reports' },
      ],
      stackedItems: true,
      connected: true,
      footer: { text: 'One live view of the full', highlight: 'revenue lifecycle' },
    },
  },
  {
    id: 'revenue-flow',
    navLabel: 'Revenue Flow',
    headline: 'See every <accent>move</accent>\nyour revenue makes',
    accentWord: 'move',
    camera: { x: -20, y: -12, scale: 1.3, rotate: -4 },
    sub: 'Revvly tracks upgrades, downgrades, churn, and expansion the moment they happen.',
    featureCardPosition: 'right',
    featureCard: {
      description:
        'Upgrades, downgrades, churn, and expansion, <strong>captured in real time.</strong>',
      overview: {
        title: 'Revenue overview',
        amount: '$12,540.52',
        subtitle: '17 new customers this month',
      },
      stats: [
        { label: 'Customer churn', value: '2.4%' },
        { label: 'Upgrades', value: '08' },
        { label: 'Downgrades', value: '05' },
      ],
    },
  },
  {
    id: 'unified-platform',
    navLabel: 'Unified Platform',
    headline: 'Revvly AI unifies your\nentire revenue',
    accentWord: 'lifecycle',
    camera: { x: -8, y: 18, scale: 1.55, rotate: 2 },
    sub: 'Subscriptions, pricing, payments, customer data, and analytics, one connected layer.',
    featureCardPosition: 'center',
    featureCard: {
      description:
        '<strong>Subscriptions, Pricing, Payments, Customer data, Revenue analytics.</strong> All connected through one unified data layer',
      subCards: [
        {
          title: 'Subscriptions',
          rows: [
            { label: 'Next renewal', value: '—' },
            { label: 'Pro plan', value: '$99/mo' },
          ],
        },
        {
          title: 'Pricing',
          rows: [
            { label: 'Pro plan', value: '' },
            { label: 'Basic', value: '' },
          ],
        },
      ],
      brandCard: true,
    },
  },
  {
    id: 'revenue-intelligence',
    navLabel: 'Revenue Intelligence',
    headline: 'Revvly surfaces the <accent>patterns</accent>\nthat grow revenue',
    accentWord: 'patterns',
    camera: { x: 22, y: -18, scale: 1.85, rotate: -3 },
    sub: 'Churn signals, expansion opportunities, and revenue trends, surfaced automatically.',
    featureCardPosition: 'left',
    featureCard: {
      description:
        'Revvly AI analyzes activity across the system to surface <strong>Churn signals, Expansion opportunities, Revenue trends (MRR, ARR)</strong>',
      dataSections: [
        {
          title: 'Churn signals',
          indicator: 'danger',
          rows: [
            { label: 'Expiring trials', value: '23' },
            { label: 'Payment failures', value: '$1,872' },
            { label: 'Cancellations', value: '08' },
          ],
        },
        {
          title: 'Expansion opportunities',
          indicator: 'warning',
          miniChart: true,
          chartCaption: 'Pro users',
        },
      ],
    },
  },
  {
    id: 'platform-modules',
    navLabel: 'Platform Modules',
    headline: 'Built to grow with your',
    accentWord: 'company',
    camera: { x: 10, y: 10, scale: 0.95, rotate: 1 },
    sub: 'Subscription management, payments, pricing, engagement, CRM, churn analysis, AI intelligence. Start with what you need. Expand as you grow.',
    featureCardPosition: 'right',
    footerCta: { label: 'Get started now', to: '/early-access' },
    featureCard: {
      description: 'Every capability, <strong>on one platform.</strong>',
      modules: ['CRM', 'Subscriptions', 'Payments', 'Pricing', 'Customer data', 'Analytics'],
    },
  },
  {
    // Closing founding-offer CTA. Registered here so the ticker, scroll
    // navigation and video parallax include it; the visual content is rendered
    // by the dedicated <FoundingOffer /> component, not from these fields.
    id: 'founding-offer',
    navLabel: 'Founding Offer',
    headline: '',
    accentWord: '',
    camera: { x: 0, y: 0, scale: 1.3 },
  },
];
